import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import { useData } from '../../context/DataContext';
import TipsterLogo from '../TipsterLogo';

export default function TipstersEditor() {
  const { tipsters, updateTipster } = useData();
  const [selected, setSelected] = useState(tipsters[0]?.id);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = tipsters.find(x => x.id === selected) || tipsters[0];
    if (t) {
      setSelected(t.id);
      setDraft({ ...t, approachText: t.approach.join('\n'), marketsText: t.markets.join(', '), stats: t.stats.length ? t.stats : [{ label: '', value: '' }, { label: '', value: '' }, { label: '', value: '' }] });
    }
  }, [tipsters, selected]);

  if (!draft) return null;
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setStat = (i, k, v) => setDraft(d => ({ ...d, stats: d.stats.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const setPlan = (i, k, v) => setDraft(d => ({ ...d, plans: d.plans.map((p, j) => (j === i ? { ...p, [k]: v } : k === 'popular' && v ? { ...p, popular: false } : p)) }));

  const save = async () => {
    const plans = draft.plans.map(p => ({ days: Number(p.days), price: Number(p.price), popular: !!p.popular })).filter(p => p.days > 0 && p.price > 0);
    if (!plans.length) return toast({ title: 'Add at least one plan', variant: 'destructive' });
    setSaving(true);
    try {
      const { approachText, marketsText, ...rest } = draft;
      await updateTipster({
        ...rest, plans,
        approach: approachText.split('\n').map(s => s.trim()).filter(Boolean),
        markets: marketsText.split(',').map(s => s.trim()).filter(Boolean),
        stats: draft.stats.filter(s => s.label && s.value)
      });
      toast({ title: 'Tipster saved', description: `${draft.name} updated on the live site.` });
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-tipsters">
      <div className="flex flex-wrap gap-2 mb-6">
        {tipsters.map(t => (
          <button key={t.id} onClick={() => setSelected(t.id)} className={`flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full border text-sm font-semibold transition-colors ${selected === t.id ? 'bg-navy text-white border-navy' : 'bg-white border-line text-slate-600 hover:border-royal'}`} data-testid={`admin-tipster-tab-${t.id}`}>
            <TipsterLogo tipster={t} className="w-7 h-7" /> {t.name}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-line bg-white p-6 grid md:grid-cols-2 gap-4">
        <div><Label>Name</Label><Input data-testid="admin-tipster-name" value={draft.name} onChange={e => set('name', e.target.value)} className="mt-1 h-11" /></div>
        <div><Label>Format label</Label><Input data-testid="admin-tipster-format-label" value={draft.formatLabel} onChange={e => set('formatLabel', e.target.value)} className="mt-1 h-11" /></div>
        <div className="md:col-span-2"><Label>Tagline</Label><Input data-testid="admin-tipster-tagline" value={draft.tagline} onChange={e => set('tagline', e.target.value)} className="mt-1 h-11" /></div>
        <div className="md:col-span-2"><Label>Description</Label><Textarea data-testid="admin-tipster-description" rows={4} value={draft.description} onChange={e => set('description', e.target.value)} className="mt-1" /></div>
        <div><Label>How it works (one point per line)</Label><Textarea data-testid="admin-tipster-approach" rows={5} value={draft.approachText} onChange={e => set('approachText', e.target.value)} className="mt-1" /></div>
        <div><Label>Markets (comma separated)</Label><Textarea data-testid="admin-tipster-markets" rows={5} value={draft.marketsText} onChange={e => set('marketsText', e.target.value)} className="mt-1" /></div>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between"><Label>Plans (days · price in €)</Label>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => set('plans', [...draft.plans, { days: '', price: '', popular: false }])} data-testid="admin-tipster-add-plan"><Plus className="w-3.5 h-3.5 mr-1" /> Add plan</Button>
          </div>
          <div className="mt-2 grid sm:grid-cols-3 gap-3">
            {draft.plans.map((p, i) => (
              <div key={i} className="rounded-xl border border-line p-3 space-y-2" data-testid={`admin-plan-${i}`}>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid={`admin-plan-days-${i}`} type="number" min="1" value={p.days} onChange={e => setPlan(i, 'days', e.target.value)} placeholder="Days" className="h-9 text-sm font-mono" />
                  <Input data-testid={`admin-plan-price-${i}`} type="number" min="1" value={p.price} onChange={e => setPlan(i, 'price', e.target.value)} placeholder="€" className="h-9 text-sm font-mono" />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <label className="flex items-center gap-2 cursor-pointer"><Switch checked={!!p.popular} onCheckedChange={v => setPlan(i, 'popular', v)} data-testid={`admin-plan-popular-${i}`} /> Most popular</label>
                  <button type="button" onClick={() => set('plans', draft.plans.filter((_, j) => j !== i))} className="text-loss hover:underline flex items-center gap-1" data-testid={`admin-plan-remove-${i}`}><Trash2 className="w-3 h-3" /> remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <Label>Statistics (label · value)</Label>
          <div className="mt-2 grid sm:grid-cols-3 gap-3">
            {draft.stats.map((s, i) => (
              <div key={i} className="rounded-xl border border-line p-3 space-y-2">
                <Input data-testid={`admin-tipster-stat-label-${i}`} value={s.label} onChange={e => setStat(i, 'label', e.target.value)} placeholder="Label" className="h-9 text-sm" />
                <Input data-testid={`admin-tipster-stat-value-${i}`} value={s.value} onChange={e => setStat(i, 'value', e.target.value)} placeholder="Value" className="h-9 text-sm font-mono" />
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-royal hover:bg-royal-dark text-white rounded-full px-6" data-testid="admin-tipster-save"><Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save tipster'}</Button>
        </div>
      </div>
    </div>
  );
}
